import React from "react";
import globals from '../Globals'
import api from "../Auth/Api";
import { Box, Typography, Paper, List, ListItem, ListItemText, Button, IconButton, TextField, Dialog, DialogContent, DialogActions, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, DialogContentText, Tabs, Tab } from '@mui/material';
import { PageContainer, FormDialog } from "../Layout/Common";
import { ExpandMore, ExpandLess, Remove, Edit, Check } from '@mui/icons-material';


export default class AdminsPage extends React.Component {
    state = {
        userData: null,
        showDeleted: false,
        foldedUsers: {},
        renamingDevice: null,
        renamingValue: "",
        renamingUserId: null,
        renamingUserValue: "",
        addUserDialogOpen: false,
        newUserName: "",
        newUserEmail: "",
        newUserIsBot: false,
        botKeyDialogOpen: false,
        botKeyValue: "",
        changeRolesDialogOpen: false,
        rolesEditUser: null,
        roleSelections: {},
        rolesTab: 0,
        notifDialogOpen: false,
        notifEditUser: null,
        notifSelections: {},
        notifTab: 0,
        locationDialogOpen: false,
        locationDialogContent: ""
    }
    openLocationDialog = (location) => {
        const { query, status, lat, lon, ...rest } = location;
        const cleaned = {
            ip: query,
            ...rest
        };

        this.setState({
            locationDialogOpen: true,
            locationDialogContent: {
                ...cleaned,
                map: { lat, lon }
            }
        });
    }

    closeLocationDialog = () => {
        this.setState({ locationDialogOpen: false });
    }

    // Users start folded unless they have a device pending authorization
    isUserFolded = (user) => {
        const stored = this.state.foldedUsers[user.id];
        if (stored !== undefined) return stored;
        const hasDevices = user.devices.length > 0;
        const hasPendingAuthorization = user.devices.some(device => !device.authorized);
        return !hasDevices || !hasPendingAuthorization;
    }

    toggleUserFold = (user) => {
        const folded = this.isUserFolded(user);
        this.setState(prev => ({
            foldedUsers: {
                ...prev.foldedUsers,
                [user.id]: !folded
            }
        }));
    }

    performAction = (description, ...parts) =>
        api.users(description, parts).then(response => {
            this.setState({ userData: response });
            return response;
        });

    getAdminTitleIds = () => {
        const titlesDict = globals.loginData?.titles?.titles ?? {};
        const allTitleIds = Object.keys(titlesDict);

        // Logged-in user's roles per title
        const myRolesByTitle = globals.loginData?.titles?.roles ?? {};

        const hasAdmin = (raw) => {
            if (!raw) return false;
            // Expected shape: array of strings (e.g. ["admin", "#players"])
            if (Array.isArray(raw)) return raw.includes("admin");
            // Fallback: object-map (e.g. {"admin":true})
            if (typeof raw === "object") return raw["admin"] === true;
            return false;
        };

        return allTitleIds.filter(tid => hasAdmin(myRolesByTitle[tid]));
    }

    isMaster = () => globals.loginData?.titles?.isMaster === true;

    // The backend's service registry (rolesDefinition.services): roles grouped by the service that offers them.
    getRoleServices = () => globals.loginData?.titles?.rolesDefinition?.services ?? [];

    // Tabs of the roles dialog: titles I administer, plus "global" (roles of the global services) for masters.
    getRoleTitleIds = () => {
        const list = this.getAdminTitleIds();
        if (this.isMaster()) list.push("global");
        return list;
    }

    // Roles offered under a tab, grouped by service: the global services for "global", otherwise the services the
    // title has (titles[id].services). Mirrors getOfferedKinds for notifications.
    getOfferedRoles = (scopeKey) => {
        const services = scopeKey === "global" ? null : (globals.loginData?.titles?.titles?.[scopeKey]?.services ?? []);
        return this.getRoleServices()
            .filter(s => scopeKey === "global" ? s.global : (!s.global && services.includes(s.id)))
            .map(s => ({ service: s.id, serviceTitle: s.title, roles: s.roles ?? [] }))
            .filter(g => g.roles.length > 0);
    }

    // A role without a view variant ("#role" means nothing to any permission, or admin): either granted or not.
    isEditOnlyRole = (r) => r.key === "admin" || r.viewable === false;

    // Tabs of the notifications dialog: titles I administer, plus my own titles when editing myself, plus "global"
    // (own subscriptions, or a master editing anyone).
    getNotifTitleIds = (isSelf) => {
        const titlesDict = globals.loginData?.titles?.titles ?? {};
        const ids = new Set(this.getAdminTitleIds());
        if (isSelf) Object.keys(globals.loginData?.titles?.roles ?? {}).forEach(t => { if (titlesDict[t]) ids.add(t); });
        const list = Object.keys(titlesDict).filter(t => ids.has(t));
        if (isSelf || this.isMaster()) list.push("global");
        return list;
    }

    // Kinds offered under a tab, grouped by service: the global kinds for "global", otherwise the title-scoped kinds of
    // the services the title subscribes to (titles[id].services). Each kind carries its full id "service.key".
    getOfferedKinds = (scopeKey) => {
        const registry = globals.loginData?.titles?.notificationKinds?.services ?? [];
        const services = scopeKey === "global" ? null : (globals.loginData?.titles?.titles?.[scopeKey]?.services ?? []);
        return registry
            .filter(s => scopeKey === "global" || services.includes(s.id))
            .map(s => ({ service: s.id, serviceTitle: s.title, kinds: (s.kinds ?? []).filter(k => scopeKey === "global" ? k.scope === "global" : k.scope === "title") }))
            .filter(g => g.kinds.length > 0);
    }

    renderUsers = () => {
        const { users } = this.state.userData;
        // Real users have an email address; API / agent bots use a random key (no '@') as their email.
        const isBot = (u) => !String(u.email || '').includes('@');
        const allowedUsers = users.filter(u => u.allowed && !isBot(u));
        const allowedBots = users.filter(u => u.allowed && isBot(u));
        const disallowedUsers = users.filter(u => !u.allowed);
        const canAddUsers = globals.loginData?.titles?.hasAdminRole === true;

        const pendingAuthUsers = users.filter(user =>
            user.devices.some(device => !device.authorized)
        );

        const renderUserCard = (user, isDisallowed = false) => {
            const hasDevices = user.devices.length > 0;
            const isViewOnly = user.viewOnly === true;
            const isSelf = String(globals.loginData?.id) === String(user.id);

            return (
                <Paper
                    key={user.id}
                    elevation={3}
                    sx={{
                        marginBottom: 3,
                        padding: 2,
                        backgroundColor: isDisallowed ? '#f0f0f0' : 'white'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: this.isUserFolded(user) ? 0 : 1
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', opacity: isDisallowed ? 0.6 : 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {this.state.renamingUserId === user.id ? (
                                        <>
                                            <TextField
                                                size="small"
                                                variant="standard"
                                                value={this.state.renamingUserValue}
                                                onChange={this.handleRenameUserChange}
                                                sx={{ maxWidth: 240 }}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={this.handleRenameUserSubmit}
                                                sx={{ marginLeft: 1 }}
                                                aria-label="confirm-rename-user"
                                            >
                                                <Check fontSize="small" />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="h6">
                                                {user.name}
                                            </Typography>
                                            {(!isViewOnly || isSelf) && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => this.handleRenameUser(user.id, user.name)}
                                                    sx={{ marginLeft: 1 }}
                                                    aria-label="rename-user"
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            )}
                                        </>
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {user.email}
                                </Typography>
                            </Box>
                            {hasDevices ? (
                                <IconButton size="small" onClick={() => this.toggleUserFold(user)}>
                                    {this.isUserFolded(user) ? <ExpandMore /> : <ExpandLess />}
                                </IconButton>
                            ) : (
                                <Remove sx={{ marginLeft: 1 }} />
                            )}
                        </Box>
                        <Box>
                            {!isViewOnly && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="medium"
                                    onClick={() => this.handleChangeRoles(user.id)}
                                    sx={{ minHeight: '40px', marginRight: 1 }}
                                >
                                    Change Roles
                                </Button>
                            )}
                            {(!isViewOnly || isSelf) && String(user.email || '').includes('@') && (   // bots have no mailbox
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="medium"
                                    onClick={() => this.handleSetNotifications(user.id)}
                                    sx={{ minHeight: '40px', marginRight: 1 }}
                                >
                                    Set Notifications
                                </Button>
                            )}
                            {!isViewOnly && (
                                <Button
                                    variant="outlined"
                                    color={isDisallowed ? 'success' : 'error'}
                                    size="medium"
                                    onClick={() => isDisallowed ? this.handleRestoreUser(user.id) : this.handleDeleteUser(user.id)}
                                    sx={{ minHeight: '40px' }}
                                >
                                    {isDisallowed ? 'Restore' : 'Delete'}
                                </Button>
                            )}
                        </Box>
                    </Box>
                    {hasDevices && !this.isUserFolded(user) && (
                        <Box sx={{ opacity: isDisallowed ? 0.6 : 1, marginTop: 1 }}>
                            <List>
                                {user.devices.map((device, index) => (
                                    <ListItem
                                        key={device.id}
                                        divider={index !== user.devices.length - 1}
                                        secondaryAction={
                                            <Box>
                                                {!device.authorized && (
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="medium"
                                                        onClick={() => this.handleAuthorize(user.id, device.id)}
                                                        sx={{ minHeight: '40px', marginRight: 1 }}
                                                    >
                                                        Authorize
                                                    </Button>
                                                )}
                                                {(!isViewOnly || isSelf) && (
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="medium"
                                                        onClick={() => this.handleDeleteDevice(user.id, device.id)}
                                                        sx={{ minHeight: '40px' }}
                                                    >
                                                        Delete
                                                    </Button>
                                                )}
                                            </Box>
                                        }
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    Device:&nbsp;
                                                    {this.state.renamingDevice?.userId === user.id && this.state.renamingDevice?.deviceId === device.id ? (
                                                        <>
                                                            <TextField
                                                                size="small"
                                                                variant="standard"
                                                                value={this.state.renamingValue}
                                                                onChange={this.handleRenameChange}
                                                                sx={{ maxWidth: 200 }}
                                                            />
                                                            <IconButton
                                                                size="small"
                                                                onClick={this.handleRenameSubmit}
                                                                sx={{ marginLeft: 1 }}
                                                            >
                                                                <Check fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {device.name ?? device.id}
                                                            <IconButton
                                                                size="small"
                                                                sx={{ marginLeft: 1 }}
                                                                onClick={() => this.handleRenameDevice(user.id, device.id, device.name)}
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <>
                                                    Logged In: {device.loggedIn ? 'Yes' : 'No'} | Last Interaction: {device.lastInteraction}
                                                    {device.location && (
                                                        <>
                                                            {' | '}
                                                            <Typography
                                                                variant="body2"
                                                                component="span"
                                                                onClick={() => this.openLocationDialog(device.location)}
                                                                sx={{ cursor: 'pointer', textDecoration: 'underline', display: 'inline', marginLeft: '4px' }}
                                                            >
                                                                {device.location.country}, {device.location.regionName}, {device.location.city}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}
                </Paper>
            );
        };

        return (
            <PageContainer>
                {pendingAuthUsers.length > 0 && (
                    <Typography sx={{ backgroundColor: '#fff3cd', color: '#856404', padding: 2, borderRadius: 1, marginBottom: 2 }}>
                        Pending Authorization: {pendingAuthUsers.map(u => `${u.name} (${u.email})`).join(', ')}
                    </Typography>
                )}
                <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>Users</Typography>
                {allowedUsers.map(user => renderUserCard(user))}
                {allowedBots.length > 0 && <>
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>API / Agents <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>(bot accounts — key credential, no email)</Typography></Typography>
                    {allowedBots.map(user => renderUserCard(user))}
                </>}
                {canAddUsers && (
                    <Box sx={{ textAlign: 'center', marginY: 2 }}>
                        <Button variant="contained" color="primary" onClick={this.handleAddUser}>
                            Add User
                        </Button>
                    </Box>
                )}
                {disallowedUsers.length > 0 && (
                    <>
                        <Box sx={{ textAlign: 'left', marginBottom: 2 }}>
                            <Button
                                variant="text"
                                onClick={() => this.setState(prev => ({ showDeleted: !prev.showDeleted }))}
                            >
                                {this.state.showDeleted ? 'Hide Deleted Users' : 'Show Deleted Users'}
                            </Button>
                        </Box>
                        {this.state.showDeleted && (
                            <>
                                {disallowedUsers.map(user => renderUserCard(user, true))}
                            </>
                        )}
                    </>
                )}
                <FormDialog
                    open={this.state.addUserDialogOpen}
                    title="Add New User"
                    onClose={this.handleCancelAddUser}
                    onConfirm={this.handleConfirmAddUser}
                    confirmText="Add"
                >
                    <TextField
                        margin="dense"
                        label="Name"
                        fullWidth
                        value={this.state.newUserName}
                        onChange={(e) => this.setState({ newUserName: e.target.value })}
                    />
                    {!this.state.newUserIsBot && (
                        <TextField
                            margin="dense"
                            label="Email"
                            fullWidth
                            value={this.state.newUserEmail}
                            onChange={(e) => this.setState({ newUserEmail: e.target.value })}
                        />
                    )}
                    <Box sx={{ mt: 1 }}>
                        <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                            <Checkbox
                                checked={this.state.newUserIsBot}
                                onChange={(e) => this.setState({ newUserIsBot: e.target.checked })}
                            />
                            <span>Bot User (API access)</span>
                        </label>
                        {this.state.newUserIsBot && (
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                A random login key will be generated in place of an email.
                            </Typography>
                        )}
                    </Box>
                </FormDialog>
                <FormDialog
                    open={this.state.botKeyDialogOpen}
                    title="Bot login key"
                    onClose={this.handleCloseBotKey}
                    cancelText="Done"
                    hideConfirm
                >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        This is the bot's only login secret — copy it into the CLI config now, or into Unity
                        (Project Settings → Aurecas Lib → Backend → API user key). You can also find it later as
                        the user's email.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        value={this.state.botKeyValue}
                        InputProps={{ readOnly: true }}
                    />
                    <Button
                        sx={{ mt: 1 }}
                        onClick={() => { navigator.clipboard?.writeText(this.state.botKeyValue); }}
                    >
                        Copy key
                    </Button>
                </FormDialog>
                <Dialog
                    open={this.state.changeRolesDialogOpen}
                    onClose={this.handleCancelRoles}
                    maxWidth="sm"
                    fullWidth
                >
                    {/* Roles grouped by service; a title tab lists the services the title has, the Global tab (masters) the global services. */}
                    <DialogContent>
                        {(() => {
                            const titlesDict = globals.loginData?.titles?.titles ?? {};
                            const user = this.state.rolesEditUser;
                            const scopeKeys = this.getRoleTitleIds();

                            const tabIndex = Math.min(this.state.rolesTab ?? 0, Math.max(scopeKeys.length - 1, 0));
                            const current = scopeKeys.length > 0 ? scopeKeys[tabIndex] : null;
                            const groups = current ? this.getOfferedRoles(current) : [];

                            return (
                                <>
                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Roles for {user?.name}</Typography>
                                    <Tabs
                                        value={tabIndex}
                                        onChange={(e, v) => this.setState({ rolesTab: v })}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        sx={{ marginBottom: 1 }}
                                    >
                                        {scopeKeys.map((key) => {
                                            const info = titlesDict[key];
                                            const label = key === "global" ? "Global" : (info?.title ?? info?.name ?? key);
                                            return <Tab key={key} label={label} />;
                                        })}
                                    </Tabs>

                                    {groups.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            {current === "global" ? "No global service offers roles." : "This title offers no roles."}
                                        </Typography>
                                    ) : (
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><b>Role</b></TableCell>
                                                    <TableCell align="center" sx={{ width: '80px' }}><b>View</b></TableCell>
                                                    <TableCell align="center" sx={{ width: '80px' }}><b>Edit</b></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {groups.map((g) => (
                                                    <React.Fragment key={g.service}>
                                                        <TableRow sx={{ backgroundColor: '#eeeeee' }}>
                                                            <TableCell colSpan={3}><b>{g.serviceTitle}</b></TableCell>
                                                        </TableRow>
                                                        {g.roles.map((r, index) => {
                                                            const editOnly = this.isEditOnlyRole(r);
                                                            const sel = this.state.roleSelections?.[current]?.[r.key];
                                                            return (
                                                                <TableRow
                                                                    key={r.key}
                                                                    sx={{
                                                                        backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                                                                        '& td': { paddingTop: '4px', paddingBottom: '4px' }
                                                                    }}
                                                                >
                                                                    <TableCell>
                                                                        <Tooltip title={r.description ?? ""} placement="top">
                                                                            <span>{r.title ?? r.key}</span>
                                                                        </Tooltip>
                                                                    </TableCell>
                                                                    <TableCell align="center" sx={{ width: '80px' }}>
                                                                        {!editOnly && (
                                                                            <Checkbox
                                                                                checked={sel?.view || false}
                                                                                onChange={() => this.handleRoleToggle(current, r.key, "view", editOnly)}
                                                                            />
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell align="center" sx={{ width: '80px' }}>
                                                                        <Checkbox
                                                                            checked={sel?.edit || false}
                                                                            onChange={() => this.handleRoleToggle(current, r.key, "edit", editOnly)}
                                                                        />
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </>
                            );
                        })()}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleCancelRoles}>Cancel</Button>
                        <Button variant="contained" onClick={this.handleConfirmRoles}>Save</Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={this.state.notifDialogOpen}
                    onClose={this.handleCancelNotifs}
                    maxWidth="sm"
                    fullWidth
                >
                    {/* Same Tabs + Table shell as the roles dialog above; kept inline (different columns and grouping). */}
                    <DialogContent>
                        {(() => {
                            const titlesDict = globals.loginData?.titles?.titles ?? {};
                            const user = this.state.notifEditUser;
                            const scopeKeys = user ? this.getNotifTitleIds(String(globals.loginData?.id) === String(user.id)) : [];

                            const tabIndex = Math.min(this.state.notifTab ?? 0, Math.max(scopeKeys.length - 1, 0));
                            const current = scopeKeys.length > 0 ? scopeKeys[tabIndex] : null;
                            const groups = current ? this.getOfferedKinds(current) : [];

                            return (
                                <>
                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Notifications for {user?.name}</Typography>
                                    <Tabs
                                        value={tabIndex}
                                        onChange={(e, v) => this.setState({ notifTab: v })}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        sx={{ marginBottom: 1 }}
                                    >
                                        {scopeKeys.map((key) => {
                                            const info = titlesDict[key];
                                            const label = key === "global" ? "Global" : (info?.title ?? info?.name ?? key);
                                            return <Tab key={key} label={label} />;
                                        })}
                                    </Tabs>

                                    {groups.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            {current === "global" ? "No global notifications are registered." : "This title offers no notifications."}
                                        </Typography>
                                    ) : (
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><b>Notification</b></TableCell>
                                                    <TableCell align="center" sx={{ width: '80px' }}><b>On</b></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {groups.map((g) => (
                                                    <React.Fragment key={g.service}>
                                                        <TableRow sx={{ backgroundColor: '#eeeeee' }}>
                                                            <TableCell colSpan={2}><b>{g.serviceTitle}</b></TableCell>
                                                        </TableRow>
                                                        {g.kinds.map((k, index) => (
                                                            <TableRow
                                                                key={k.id}
                                                                sx={{
                                                                    backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                                                                    '& td': { paddingTop: '4px', paddingBottom: '4px' }
                                                                }}
                                                            >
                                                                <TableCell>
                                                                    <Tooltip title={k.description ?? ""} placement="top">
                                                                        <span>{k.title}</span>
                                                                    </Tooltip>
                                                                </TableCell>
                                                                <TableCell align="center" sx={{ width: '80px' }}>
                                                                    <Checkbox
                                                                        checked={!!this.state.notifSelections?.[current]?.[k.id]}
                                                                        onChange={() => this.handleNotifToggle(current, k.id)}
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </>
                            );
                        })()}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleCancelNotifs}>Cancel</Button>
                        <Button variant="contained" onClick={this.handleConfirmNotifs}>Save</Button>
                    </DialogActions>
                </Dialog>
                <FormDialog
                    open={this.state.locationDialogOpen}
                    title="Location Details"
                    onClose={this.closeLocationDialog}
                    maxWidth="sm"
                    fullWidth
                    hideConfirm
                    cancelText="Close"
                >
                    <DialogContentText component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                        {this.state.locationDialogContent && (() => {
                            const l = this.state.locationDialogContent;
                            return [
                                `IP: ${l.ip}`,
                                `Country: ${l.country} (${l.countryCode})`,
                                `Region: ${l.regionName} (${l.region})`,
                                `City: ${l.city}`,
                                `ZIP: ${l.zip}`,
                                `Time Zone: ${l.timezone}`,
                                `ISP: ${l.isp}`,
                                `ORG: ${l.org}`
                            ].join('\n');
                        })()}
                    </DialogContentText>
                    {this.state.locationDialogContent && this.state.locationDialogContent.map && (
                        <Box sx={{ marginTop: 2 }}>
                            <iframe
                                title="location-map"
                                width="100%"
                                height="250"
                                frameBorder="0"
                                style={{ border: 0 }}
                                src={`https://www.google.com/maps?q=${this.state.locationDialogContent.map.lat},${this.state.locationDialogContent.map.lon}&z=14&output=embed`}
                                allowFullScreen
                            />
                        </Box>
                    )}
                </FormDialog>
            </PageContainer>
        );
    }

    handleAuthorize = (userId, deviceId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        this.performAction(`Authorize device for ${user.name}`, "authorize-device", deviceId);
    }

    handleDeleteDevice = (userId, deviceId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        this.performAction(`Delete device for ${user.name}`, "delete-device", deviceId);
    }

    handleDeleteUser = (userId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        this.performAction(`Delete user ${user.name}`, "delete-user", userId);
    }

    handleChangeRoles = (userId) => {
        const user = this.state.userData.users.find(u => u.id === userId);

        // user.roles is Dictionary<titleId | "global", HashSet<string>> (serialized as array or object-map)
        const rolesByKey = user.roles && typeof user.roles === 'object' ? user.roles : {};

        // Seed {view, edit} for every role offered under each tab (roles the tab no longer offers are dropped on save)
        const selections = {};
        for (const key of this.getRoleTitleIds()) {
            const raw = rolesByKey[key];
            const userRoles = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.keys(raw) : []);

            const perKey = {};
            this.getOfferedRoles(key).forEach(g => g.roles.forEach(r => {
                const edit = userRoles.includes(r.key);
                const view = this.isEditOnlyRole(r) ? edit : (edit || userRoles.includes(`#${r.key}`));
                perKey[r.key] = { view, edit };
            }));
            selections[key] = perKey;
        }

        this.setState({
            changeRolesDialogOpen: true,
            rolesEditUser: user,
            roleSelections: selections,
            rolesTab: 0,
            renamingUserId: null,
            renamingUserValue: "",
            renamingDevice: null,
            renamingValue: "",
        });
    }

    handleSetNotifications = (userId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        const isSelf = String(globals.loginData?.id) === String(user.id);

        const selections = {};
        for (const key of this.getNotifTitleIds(isSelf)) {
            const raw = user.notifications?.[key];
            const current = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.keys(raw) : []);
            const perKey = {};
            this.getOfferedKinds(key).forEach(g => g.kinds.forEach(k => { perKey[k.id] = current.includes(k.id); }));
            selections[key] = perKey;
        }

        this.setState({
            notifDialogOpen: true,
            notifEditUser: user,
            notifSelections: selections,
            notifTab: 0,
            renamingUserId: null,
            renamingUserValue: "",
            renamingDevice: null,
            renamingValue: "",
        });
    }

    handleNotifToggle = (scopeKey, kindId) => {
        this.setState(prev => ({
            notifSelections: {
                ...prev.notifSelections,
                [scopeKey]: { ...(prev.notifSelections?.[scopeKey] ?? {}), [kindId]: !prev.notifSelections?.[scopeKey]?.[kindId] },
            },
        }));
    }

    handleConfirmNotifs = () => {
        const { notifEditUser, notifSelections } = this.state;

        // Payload: { [titleId | "global"]: ["service.kind", ...] } — every tab shown is sent, so an empty list clears it.
        const payload = {};
        for (const [key, perKey] of Object.entries(notifSelections ?? {})) {
            payload[key] = Object.entries(perKey).filter(([, on]) => on).map(([id]) => id);
        }

        api.users(`Update notifications for ${notifEditUser.name}`, ["set-notifications", notifEditUser.id], { payload })
            .then(response => {
                if (response && response.users) this.setState({ userData: response });
            })
            .catch(() => {})
            .finally(() => {
                this.setState({ notifDialogOpen: false, notifEditUser: null, notifSelections: {} });
            });
    }

    handleCancelNotifs = () => {
        this.setState({ notifDialogOpen: false, notifEditUser: null, notifSelections: {} });
    }

    handleRestoreUser = (userId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        this.performAction(`Restore user ${user.name}`, "restore-user", userId);
    }

    handleAddUser = () => {
        this.setState({ addUserDialogOpen: true });
    }

    // 512-bit random key, standard base64 — used as the "email" for bot users (no '@',
    // so the backend's debug:<key> bypass accepts it in production). This is the bot's login secret.
    generateBotKey = () => {
        const bytes = new Uint8Array(64);
        window.crypto.getRandomValues(bytes);
        let bin = "";
        for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
        return btoa(bin);
    }

    openRolesForEmail = (email) => {
        const emailLower = (email || "").trim().toLowerCase();
        const added = (this.state.userData?.users ?? []).find(
            u => (u.email || "").trim().toLowerCase() === emailLower
        );
        if (added) this.handleChangeRoles(added.id);
    }

    handleConfirmAddUser = () => {
        const { newUserName, newUserEmail, newUserIsBot } = this.state;
        const userName = (newUserName || "").trim().replace(/\s+/g, "_");
        const emailToUse = newUserIsBot ? this.generateBotKey() : newUserEmail;

        this.performAction(`Add user ${userName}`, "add-user", userName, emailToUse)
            .then(() => {
                this.setState(
                    {
                        addUserDialogOpen: false,
                        newUserName: "",
                        newUserEmail: "",
                        newUserIsBot: false,
                        // For bots, show the key first; roles open after that dialog closes.
                        ...(newUserIsBot ? { botKeyDialogOpen: true, botKeyValue: emailToUse } : {})
                    },
                    () => {
                        if (!newUserIsBot) this.openRolesForEmail(emailToUse);
                    }
                );
            })
            .catch(() => {});
    }

    handleCloseBotKey = () => {
        const email = this.state.botKeyValue;
        this.setState({ botKeyDialogOpen: false }, () => this.openRolesForEmail(email));
    }

    handleCancelAddUser = () => {
        this.setState({ addUserDialogOpen: false, newUserIsBot: false });
    }

    handleRenameDevice = (userId, deviceId, currentName) => {
        this.setState({
            renamingDevice: { userId, deviceId },
            renamingValue: (currentName || "").replace(/_/g, " ")
        });
    }

    handleRenameChange = (e) => {
        this.setState({ renamingValue: e.target.value });
    }

    handleRenameSubmit = () => {
        const { userId, deviceId } = this.state.renamingDevice;
        const newName = this.state.renamingValue.trim().replace(/\s+/g, "_");
        if (newName) {
            const user = this.state.userData.users.find(u => u.id === userId);
            this.performAction(`Rename device for ${user.name}`, "rename-device", deviceId, newName);
        }
        this.setState({ renamingDevice: null, renamingValue: "" });
    }

    handleRenameUser = (userId, currentName) => {
        this.setState({
            renamingUserId: userId,
            renamingUserValue: (currentName || "").replace(/_/g, " ")
        });
    }

    handleRenameUserChange = (e) => {
        this.setState({ renamingUserValue: e.target.value });
    }

    handleRenameUserSubmit = () => {
        const userId = this.state.renamingUserId;
        const newName = (this.state.renamingUserValue || "").trim().replace(/\s+/g, "_");

        if (userId && newName) {
            const user = this.state.userData.users.find(u => u.id === userId);
            this.performAction(`Rename user ${user?.name ?? userId}`, "rename-user", userId, newName);
        }

        this.setState({ renamingUserId: null, renamingUserValue: "" });
    }

    handleRoleToggle = (scopeKey, role, type, editOnly = false) => {
        if (!scopeKey) return;
        this.setState(prev => {
            const selections = { ...prev.roleSelections };
            const scopeSel = { ...(selections[scopeKey] ?? {}) };
            const current = { ...(scopeSel[role] ?? { view: false, edit: false }) };

            if (editOnly) {
                // No view variant: either full permission (edit) or nothing
                const next = type === 'edit' ? !current.edit : !current.view;
                current.view = next;
                current.edit = next;
            } else if (type === 'edit') {
                current.edit = !current.edit;
                if (current.edit) current.view = true;
            } else if (type === 'view') {
                current.view = !current.view;
                if (!current.view) current.edit = false;
            }

            scopeSel[role] = current;
            selections[scopeKey] = scopeSel;
            return { roleSelections: selections };
        });
    }

    handleConfirmRoles = () => {
        const { rolesEditUser, roleSelections } = this.state;

        // Build payload: { [titleId | "global"]: ["role", "#role", ...] } — every tab shown is sent, an empty list
        // clears the scope. Edit-only roles keep view === edit, so "#role" never appears for them.
        const rolesByKey = {};
        for (const key of this.getRoleTitleIds()) {
            const perKey = roleSelections?.[key] ?? {};
            rolesByKey[key] = Object.entries(perKey).flatMap(([role, perms]) => {
                if (perms?.view && perms?.edit) return [role];
                if (perms?.view) return [`#${role}`];
                return [];
            });
        }

        // Server expects JSON body containing Dictionary<string, HashSet<string>>
        api.users(`Update roles for ${rolesEditUser.name}`, ["set-roles", rolesEditUser.id], { payload: rolesByKey })
            .then(response => {
                // Keep UI consistent: update list with latest server state
                this.setState({ userData: response });
            })
            .catch(() => {})
            .finally(() => {
                this.setState({ changeRolesDialogOpen: false, rolesEditUser: null, roleSelections: {} });
            });
    }

    handleCancelRoles = () => {
        this.setState({ changeRolesDialogOpen: false, renamingUserId: null, renamingUserValue: "" });
    }

    render() {
        if (this.state.userData == null) {
            return <div>Loading...</div>;
        } else {
            return this.renderUsers();
        }
    }

    componentDidMount() {
        this.performAction("Loading", "list");
    }
}